/**
 * Paged preview of the spreadsheet document: virtual A4 sheets, optional inline
 * editing of cell values (edits mutate the model via onCellEdit — every print
 * window output derives from the current model).
 */
import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { MM_TO_PX, paginate } from './paginate';
import type { PrintPage } from './paginate';
import type {
  FilledSpreadsheetModel,
  SpreadsheetCell,
  SpreadsheetCellStyle,
  SpreadsheetSectionModel,
} from './types';

interface SpreadsheetViewProps {
  model: FilledSpreadsheetModel;
  zoomPct: number;
  editMode: boolean;
  onCellEdit: (sectionIndex: number, rowIndex: number, cellIdx: number, value: string) => void;
}

type PaddedItem =
  | { kind: 'cell'; cell: SpreadsheetCell; cellIdx: number }
  | { kind: 'gap' };

/** Lay out a section's cells across columns honoring row/col span (backend grid semantics). */
export function padSectionRows(
  section: SpreadsheetSectionModel,
  colCount: number,
): Map<number, PaddedItem[]> {
  const occupied = new Set<string>();
  const out = new Map<number, PaddedItem[]>();
  for (const row of section.rows) {
    const items: PaddedItem[] = [];
    const byCol = new Map<number, { cell: SpreadsheetCell; cellIdx: number }>();
    row.cells.forEach((cell, cellIdx) => byCol.set(cell.col, { cell, cellIdx }));
    let c = 0;
    while (c < colCount) {
      if (occupied.has(`${row.index}:${c}`)) {
        c += 1;
        continue;
      }
      const hit = byCol.get(c);
      if (!hit) {
        items.push({ kind: 'gap' });
        c += 1;
        continue;
      }
      const { cell, cellIdx } = hit;
      for (let dr = 0; dr < cell.row_span; dr += 1) {
        for (let dc = 0; dc < cell.col_span; dc += 1) {
          occupied.add(`${row.index + dr}:${c + dc}`);
        }
      }
      items.push({ kind: 'cell', cell, cellIdx });
      c += cell.col_span;
    }
    out.set(row.index, items);
  }
  return out;
}

function borderSideCss(side?: { style?: string; color?: string }): string {
  if (!side) return '1px solid #ccc';
  const style = side.style || 'thin';
  if (style === 'none') return 'none';
  const width = style === 'thick' ? 3 : style === 'medium' ? 2 : 1;
  const color = !side.color || side.color === 'divider' ? '#ccc' : side.color;
  return `${width}px solid ${color}`;
}

function cellCss(style: SpreadsheetCellStyle): React.CSSProperties {
  const css: React.CSSProperties = {
    fontSize: style.fontSize ? `${style.fontSize}pt` : '10pt',
    fontWeight: style.bold ? 700 : 400,
    fontStyle: style.italic ? 'italic' : undefined,
    textDecoration: style.underline ? 'underline' : undefined,
    textAlign: style.textAlign || 'left',
    verticalAlign: style.vertAlign || 'top',
    color: style.textColor || undefined,
    backgroundColor: style.fillColor || undefined,
    whiteSpace: style.wrapText ? 'pre-wrap' : undefined,
    wordBreak: style.wrapText ? 'break-word' : undefined,
    padding: '4px 6px',
  };
  if (style.textRotation !== undefined && style.textRotation !== 0) {
    css.transform = `rotate(${style.textRotation}deg)`;
    css.transformOrigin = 'center center';
  }
  if (style.border) {
    css.borderTop = borderSideCss(style.border.top);
    css.borderRight = borderSideCss(style.border.right);
    css.borderBottom = borderSideCss(style.border.bottom);
    css.borderLeft = borderSideCss(style.border.left);
  } else {
    css.border = '1px solid #e0e0e0';
  }
  return css;
}

export function SpreadsheetView({ model, zoomPct, editMode, onCellEdit }: SpreadsheetViewProps) {
  const settings = model.page_settings;
  const pages: PrintPage[] = useMemo(() => paginate(model, settings), [model, settings]);

  const colCount = model.columns.length || 1;
  const paddedBySection = useMemo(
    () => model.sections.map((s) => padSectionRows(s, colCount)),
    [model.sections, colCount],
  );

  const landscape = settings.orientation === 'landscape';
  const pageWmm = landscape ? 297 : 210;
  const pageHmm = landscape ? 210 : 297;
  const zoom = zoomPct / 100;
  const printScale = Math.max(settings.scale_pct, 1) / 100;
  const widthTotal = model.columns.reduce((s, c) => s + c.width, 0) || 1;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 3 }}>
      {pages.map((page, pageIdx) => (
        <Box key={pageIdx} sx={{ position: 'relative' }}>
          <Paper
            elevation={4}
            square
            data-testid={`print-page-${pageIdx}`}
            sx={{
              width: pageWmm * MM_TO_PX * zoom,
              height: pageHmm * MM_TO_PX * zoom,
              overflow: 'hidden',
              backgroundColor: '#fff',
              color: '#212121',
            }}
          >
            <Box
              sx={{
                transform: `scale(${zoom * printScale})`,
                transformOrigin: 'top left',
                width: (pageWmm * MM_TO_PX) / printScale,
                pt: `${settings.margins_mm.top * MM_TO_PX}px`,
                pl: `${settings.margins_mm.left * MM_TO_PX}px`,
                pr: `${settings.margins_mm.right * MM_TO_PX}px`,
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  {model.columns.map((c) => (
                    <col key={c.index} style={{ width: `${(c.width / widthTotal) * 100}%` }} />
                  ))}
                </colgroup>
                <tbody>
                  {page.rows.map(({ sectionIndex, rowIndex }) => {
                    const row = model.sections[sectionIndex]?.rows[rowIndex];
                    const items = paddedBySection[sectionIndex]?.get(row?.index ?? -1) || [];
                    if (!row) return null;
                    return (
                      <tr key={`${sectionIndex}-${rowIndex}`} style={{ height: row.height_px }}>
                        {items.map((item, i) => {
                          if (item.kind === 'gap') {
                            return <td key={`gap-${i}`} style={{ border: '1px solid #f0f0f0' }} />;
                          }
                          // SSRF guard: only data: URIs render as <img> (mirrors the backend
                          // render-layer gate) — a remote image_src falls back to plain text
                          // instead of turning the preview into a remote-image beacon.
                          const isMedia =
                            (item.cell.content_type === 'image' ||
                              item.cell.content_type === 'barcode') &&
                            !!item.cell.image_src &&
                            item.cell.image_src.startsWith('data:');
                          return (
                            <td
                              key={`cell-${i}`}
                              colSpan={item.cell.col_span}
                              rowSpan={item.cell.row_span}
                              style={cellCss(item.cell.style)}
                              data-testid={`cell-${sectionIndex}-${rowIndex}-${item.cellIdx}`}
                              contentEditable={editMode && !isMedia}
                              suppressContentEditableWarning
                              onBlur={(e) => {
                                if (!editMode || isMedia) return;
                                const next = e.currentTarget.textContent ?? '';
                                if (next !== item.cell.value) {
                                  onCellEdit(sectionIndex, rowIndex, item.cellIdx, next);
                                }
                              }}
                            >
                              {isMedia ? (
                                <img
                                  src={item.cell.image_src}
                                  alt=""
                                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                                />
                              ) : (
                                item.cell.value
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          </Paper>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ position: 'absolute', bottom: -20, right: 4 }}
          >
            {pageIdx + 1} / {pages.length}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
